import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'motion/react';
import { toast } from 'sonner';
import { useAuthRemount } from '../App';
import { BACKEND_URL } from '../config';
import LandingHeader from '../components/LandingHeader';
import Footer from '../components/Footer';
import SEO from '../components/SEO';
import { useAffiliateTracking } from '../hooks/useAffiliateTracking';

import {
  CheckCircle,
  CircleNotch,
  Envelope,
  Eye,
  EyeSlash,
  Lock,
  User,
  WarningCircle
} from '@phosphor-icons/react';
const Register = () => {
  const navigate = useNavigate();
  const forceAuthRemount = useAuthRemount();
  const { affiliateCode, affiliateValid, trackAttribution } = useAffiliateTracking();
  
  // Check for invitation token in URL
  const searchParams = new URLSearchParams(window.location.search);
  const invitationToken = searchParams.get('invitation_token');
  const emailFromInvite = searchParams.get('email');
  const isInvitedUser = !!invitationToken;
  
  // Check for blueprint draft token (from blueprint generator flow)
  const blueprintDraftToken = searchParams.get('draft') || sessionStorage.getItem('blueprint_draft_token');
  const isFromBlueprint = !!blueprintDraftToken;
  
  const [formData, setFormData] = useState({
    name: '',
    email: emailFromInvite || '',
    password: '',
    confirmPassword: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});

  const passwordRequirements = [
    { label: 'At least 12 characters', test: (pwd) => pwd.length >= 12 },
    { label: 'Contains uppercase letter', test: (pwd) => /[A-Z]/.test(pwd) },
    { label: 'Contains lowercase letter', test: (pwd) => /[a-z]/.test(pwd) },
    { label: 'Contains number', test: (pwd) => /[0-9]/.test(pwd) },
    { label: 'Contains special character', test: (pwd) => /[!@#$%^&*(),.?":{}|<>_\-+=[\]\\/'~`]/.test(pwd) },
    { label: 'No common passwords', test: (pwd) => !isCommonPassword(pwd) }
  ];

  const commonPasswords = [
    'password', '123456', '12345678', 'qwerty', 'abc123', 'monkey', '1234567', 
    'letmein', 'trustno1', 'dragon', 'baseball', 'iloveyou', 'master', 'sunshine',
    'ashley', 'bailey', 'passw0rd', 'shadow', '123123', '654321', 'superman',
    'qazwsx', 'michael', 'football', 'welcome', 'jesus', 'ninja', 'mustang',
    'password1', '123456789', '12345', 'password123'
  ];

  const isCommonPassword = (pwd) => {
    const lowerPwd = pwd.toLowerCase();
    return commonPasswords.some(common => lowerPwd.includes(common));
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Invalid email format';
    }

    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else {
      const failedRequirements = passwordRequirements.filter(req => !req.test(formData.password));
      if (failedRequirements.length > 0) {
        newErrors.password = 'Password does not meet all requirements';
      }
    }

    if (!formData.confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your password';
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    // Clear error for this field when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    // NOTE: Conversion tracking moved to AFTER successful signup response
    // Do NOT track here - this is just an attempt

    setIsSubmitting(true);

    try {
      // If user is invited, accept invitation with password
      if (isInvitedUser && invitationToken) {
        const response = await fetch(`${BACKEND_URL}/api/auth/accept-invitation`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          credentials: 'include',
          body: JSON.stringify({
            invitation_token: invitationToken,
            email: formData.email,
            name: formData.name,
            password: formData.password
          })
        });

        const data = await response.json();

        if (!response.ok) {
          throw new Error(data.detail || 'Failed to accept invitation');
        }

        // ═══════════════════════════════════════════════════════════════════════
        // GOOGLE ADS CONVERSION TRACKING - Fire on successful invitation acceptance
        // This counts as a new user signup
        // ═══════════════════════════════════════════════════════════════════════
        if (typeof window.gtag === 'function') {
          console.log('[CONVERSION TEST] Google Ads signup conversion firing');
          console.log('[CONVERSION TEST] send_to:', 'AW-17962678208/M-Z6CJvHIYlcEMDovVC');
          console.log('[CONVERSION] Google Ads signup conversion fired - invitation accepted successfully');
          window.gtag('event', 'conversion', {
            'send_to': 'AW-17962678208/M-Z6CJvHIYlcEMDovVC'
          });
        } else {
          console.warn('[CONVERSION TEST] gtag not found – Google tag not loaded');
        }

        toast.success('Account activated successfully! Redirecting...');
        
        // CRITICAL: Force AuthProvider remount to ensure clean state for new user
        console.log('[REGISTER DEBUG] Invitation accepted, forcing AuthProvider remount');
        forceAuthRemount();
        
        console.log('[REGISTER DEBUG] Before /api/auth/me for redirect', {
          timestamp: new Date().toISOString()
        });
        
        // Fetch user info to determine correct dashboard
        const meResponse = await fetch(`${BACKEND_URL}/api/auth/me`, {
          credentials: 'include'
        });
        
        let targetUrl = '/partner-pro/workspace';  // Default for members
        
        if (meResponse.ok) {
          const userData = await meResponse.json();
          const userRole = userData.role;
          const userPlan = (userData.plan || 'starter').toLowerCase();
          const isFreeTier = ['starter', 'basic', 'free'].includes(userPlan);
          
          console.log('[REGISTER DEBUG] Got user role, deciding redirect', {
            userRole,
            userPlan,
            userId: userData.id,
            timestamp: new Date().toISOString()
          });
          
          if (userRole === 'admin' || userRole === 'primary_admin') {
            targetUrl = '/dashboard';
          } else {
            // Members → workspace
            targetUrl = '/partner-pro/workspace';
          }
        }
        
        // Check for pending install intent (from marketplace)
        const installRedirect = localStorage.getItem('post_auth_redirect');
        if (installRedirect && localStorage.getItem('install_intent')) {
          targetUrl = installRedirect;
        }
        
        console.log('[REGISTER DEBUG] Navigating to:', targetUrl);
        
        // Use navigate() instead of window.location.href to avoid page reload flash
        setTimeout(() => {
          navigate(targetUrl, { replace: true });
        }, 1000);
        
      } else {
        // Regular registration flow (not invited)
        const response = await fetch(`${BACKEND_URL}/api/auth/register`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            name: formData.name,
            email: formData.email,
            password: formData.password,
            affiliate_code: affiliateCode || undefined  // Include affiliate code if present
          })
        });

        const data = await response.json();

        if (!response.ok) {
          throw new Error(data.detail || 'Registration failed');
        }

        // ═══════════════════════════════════════════════════════════════════════
        // GOOGLE ADS CONVERSION TRACKING - Fire ONLY on successful signup
        // This is the ONLY place where purchase conversion should fire
        // ═══════════════════════════════════════════════════════════════════════
        if (typeof window.gtag === 'function') {
          console.log('[CONVERSION TEST] Google Ads signup conversion firing');
          console.log('[CONVERSION TEST] send_to:', 'AW-17962678208/M-Z6CJvHIYlcEMDovVC');
          console.log('[CONVERSION] Google Ads signup conversion fired - user created successfully');
          window.gtag('event', 'conversion', {
            'send_to': 'AW-17962678208/M-Z6CJvHIYlcEMDovVC'
          });
        } else {
          console.warn('[CONVERSION TEST] gtag not found – Google tag not loaded');
        }

        // Track attribution after successful registration (backup - primary tracking is in backend)
        if (affiliateCode && data.user_id) {
          await trackAttribution(data.user_id, formData.email);
        }

        toast.success('Registration successful! Check your email to verify your account.');
        
        // If user came from blueprint flow, store the draft token for claiming after email verification
        if (blueprintDraftToken) {
          // Store in localStorage so it persists across email verification
          localStorage.setItem('pending_blueprint_claim', blueprintDraftToken);
          localStorage.setItem('post_auth_redirect', '/blueprint/continue');
          sessionStorage.removeItem('blueprint_draft_token');
        }
        
        // Redirect to verification page
        setTimeout(() => {
          navigate(`/verify-email?email=${encodeURIComponent(formData.email)}`);
        }, 2000);
      }

    } catch (error) {
      toast.error(error.message || 'Registration failed. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleGoogleLogin = () => {
    // Pass install intent redirect to Google OAuth if present
    const postAuthRedirect = localStorage.getItem('post_auth_redirect');
    const redirectParam = postAuthRedirect ? `?redirect=${encodeURIComponent(postAuthRedirect)}` : '';
    const googleAuthUrl = `${BACKEND_URL}/api/auth/google/login${redirectParam}`;
    window.location.href = googleAuthUrl;
  };

  return (
    <div className="min-h-screen bg-[#161B26]">
      <SEO 
        title="Register - Man & Machine AI Opportunity Analyzer"
        description="Create your account to access the AI Opportunity Analyzer and unlock business automation insights."
      />
      <LandingHeader />

      <div className="pt-32 pb-20">
        <div className="max-w-md mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="glass rounded-3xl p-8"
          >
            {/* Header */}
            <div className="text-center mb-8">
              <div className="w-16 h-16 rounded-full bg-[rgba(0,229,192,0.1)] flex items-center justify-center mx-auto mb-4">
                <User size={32} className="text-[#00E5C0]" />
              </div>
              <h1 className="text-3xl font-bold text-white mb-2">
                {isInvitedUser ? 'Complete Your Registration' : 'Sign Up'}
              </h1>
              <p className="text-[#A0AEC0]">
                {isInvitedUser 
                  ? 'Set up your password to activate your account' 
                  : 'Start your AI transformation journey today'}
              </p>
            </div>

            {/* Google Sign In Button - Only for non-invited users */}
            {!isInvitedUser && (
              <>
                <button
                  onClick={handleGoogleLogin}
                  className="w-full rounded-full bg-[#00E5C0] hover:bg-[#00CCB0] mb-6 transition-colors"
                >
                  <span className="flex items-center justify-center gap-3 py-4 text-[#0B0F14] font-bold text-lg">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                      <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                      <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                      <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                    </svg>
                    Continue with Google
                  </span>
                </button>

                {/* Divider */}
                <div className="relative mb-6">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-[rgba(255,255,255,0.1)]"></div>
                  </div>
                  <div className="relative flex justify-center text-sm">
                    <span className="px-4 bg-[#161B26] text-[#718096]">Or register with email</span>
                  </div>
                </div>
              </>
            )}
            
            {/* Info banner for invited users */}
            {isInvitedUser && (
              <div className="mb-6 p-4 rounded-xl bg-[#00E5C0]/10 border border-[#00E5C0]/30">
                <p className="text-sm text-[#A0AEC0] text-center">
                  ✉️ You've been invited to join. Your email is already verified. Just set a password to get started!
                </p>
              </div>
            )}

            {/* Registration Form */}
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Name Field */}
              <div>
                <label className="block text-sm font-medium text-white mb-2">
                  Full Name
                </label>
                <div className="relative">
                  <User size={20} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#718096]" />
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    className={`w-full pl-12 pr-4 py-3 rounded-xl glass text-white focus:outline-none focus:ring-2 ${
                      errors.name ? 'ring-2 ring-[#EF4444]' : 'focus:ring-[#00E5C0]'
                    } transition-all`}
                    placeholder="John Doe"
                  />
                </div>
                {errors.name && (
                  <p className="mt-1 text-sm text-[#EF4444] flex items-center gap-1">
                    <WarningCircle size={14} />
                    {errors.name}
                  </p>
                )}
              </div>

              {/* Email Field */}
              <div>
                <label className="block text-sm font-medium text-white mb-2">
                  Email Address {isInvitedUser && <span className="text-[#14F195] text-xs">(Verified)</span>}
                </label>
                <div className="relative">
                  <Envelope size={20} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#718096]" />
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    readOnly={isInvitedUser}
                    className={`w-full pl-12 pr-4 py-3 rounded-xl glass text-white focus:outline-none focus:ring-2 ${
                      errors.email ? 'ring-2 ring-[#EF4444]' : 'focus:ring-[#00E5C0]'
                    } ${isInvitedUser ? 'opacity-75 cursor-not-allowed' : ''} transition-all`}
                    placeholder="you@company.com"
                  />
                </div>
                {errors.email && (
                  <p className="mt-1 text-sm text-[#EF4444] flex items-center gap-1">
                    <WarningCircle size={14} />
                    {errors.email}
                  </p>
                )}
              </div>

              {/* Password Field */}
              <div>
                <label className="block text-sm font-medium text-white mb-2">
                  Password
                </label>
                <div className="relative">
                  <Lock size={20} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#718096]" />
                  <input
                    type={showPassword ? 'text' : 'password'}
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    className={`w-full pl-12 pr-12 py-3 rounded-xl glass text-white focus:outline-none focus:ring-2 ${
                      errors.password ? 'ring-2 ring-[#EF4444]' : 'focus:ring-[#00E5C0]'
                    } transition-all`}
                    placeholder="••••••••"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-[#718096] hover:text-white transition-colors"
                  >
                    {showPassword ? <EyeSlash size={20} /> : <Eye size={20} />}
                  </button>
                </div>
                {formData.password && (
                  <div className="mt-2 space-y-1">
                    {passwordRequirements.map((req, index) => {
                      const isValid = req.test(formData.password);
                      return (
                        <div key={index} className="flex items-center gap-2 text-xs">
                          <CheckCircle 
                            size={14} 
                            className={isValid ? 'text-[#14F195]' : 'text-[#718096]'}
                          />
                          <span className={isValid ? 'text-[#14F195]' : 'text-[#718096]'}>
                            {req.label}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Confirm Password Field */}
              <div>
                <label className="block text-sm font-medium text-white mb-2">
                  Confirm Password
                </label>
                <div className="relative">
                  <Lock size={20} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#718096]" />
                  <input
                    type={showConfirmPassword ? 'text' : 'password'}
                    name="confirmPassword"
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    className={`w-full pl-12 pr-12 py-3 rounded-xl glass text-white focus:outline-none focus:ring-2 ${
                      errors.confirmPassword ? 'ring-2 ring-[#EF4444]' : 'focus:ring-[#00E5C0]'
                    } transition-all`}
                    placeholder="••••••••"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-[#718096] hover:text-white transition-colors"
                  >
                    {showConfirmPassword ? <EyeSlash size={20} /> : <Eye size={20} />}
                  </button>
                </div>
                {errors.confirmPassword && (
                  <p className="mt-1 text-sm text-[#EF4444] flex items-center gap-1">
                    <WarningCircle size={14} />
                    {errors.confirmPassword}
                  </p>
                )}
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full rounded-full bg-[#00E5C0] hover:bg-[#00CCB0] mt-6 disabled:opacity-50 transition-colors"
              >
                <span className="flex items-center justify-center gap-2 py-4 text-[#0B0F14] font-bold text-lg">
                  {isSubmitting ? (
                    <>
                      <CircleNotch size={20} className="animate-spin" />
                      {isInvitedUser ? 'Activating Account...' : 'Creating Account...'}
                    </>
                  ) : (
                    isInvitedUser ? 'Activate Account' : 'Sign Up'
                  )}
                </span>
              </button>
            </form>

            {/* Terms */}
            <p className="text-xs text-center text-[#718096] mt-6">
              By continuing, you agree to our{' '}
              <Link to="/terms" className="text-[#00E5C0] hover:underline">Terms of Service</Link>
              {' '}and{' '}
              <Link to="/privacy" className="text-[#00E5C0] hover:underline">Privacy Policy</Link>
            </p>

            {/* Sign In Link */}
            <div className="text-center mt-6">
              <p className="text-[#A0AEC0] text-sm">
                Already have an account?{' '}
                <Link to="/login" className="text-[#00E5C0] hover:text-[#00CCB0] font-semibold transition-colors">
                  Sign in
                </Link>
              </p>
            </div>
          </motion.div>

          {/* Back to Home */}
          <div className="text-center mt-6">
            <button
              onClick={() => navigate('/')}
              className="text-[#A0AEC0] hover:text-white transition-colors text-sm"
            >
              ← Back to Home
            </button>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default Register;
